/**
 * Pending approval — /pending-approval route.
 *
 * Terminal screen in the sign-up approval gate (see RequireApproved in
 * App.tsx) for a user who has submitted the registration form and is awaiting
 * a platform admin's decision (approval_status='pending' && approval_submitted).
 *
 * "Check status" re-runs refreshIdentity() — once an admin approves them,
 * approval_status flips to 'approved' and the gate lets them through to /app on
 * the next render (no reload needed). Sign-out is always available.
 */

import { useState } from "react";
import { Clock, Loader2, LogOut, RefreshCw } from "lucide-react";

import { useAuth } from "@/lib/auth";
import { useTenantTheme } from "@/lib/tenant-theme";
import { useToast } from "@/lib/ui/toast";
import { BrandMark } from "@/lib/ui/brand-mark";
import { Button } from "@/lib/ui/button";
import { Badge } from "@/lib/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/lib/ui/card";

export function PendingApproval() {
  const { user, refreshIdentity, logout } = useAuth();
  const tenant = useTenantTheme();
  const toast = useToast();
  const [checking, setChecking] = useState(false);

  const checkStatus = async () => {
    if (checking) return;
    setChecking(true);
    try {
      await refreshIdentity();
      // If still pending after the refresh, the RequireApproved gate keeps us
      // here — let the user know nothing's changed yet.
      toast.push("info", "No change yet — we'll let you in the moment you're approved.");
    } catch {
      toast.push("error", "Couldn't check your status. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface px-4 py-10">
      <div className="mb-6 flex items-center gap-2">
        <BrandMark />
        <span className="text-base font-semibold text-on-surface">
          {tenant.productName}
        </span>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader>
          <Badge variant="info" className="w-fit uppercase tracking-wide">
            <Clock className="mr-1 size-3" />
            Pending
          </Badge>
          <CardTitle className="text-2xl">Awaiting approval</CardTitle>
          <CardDescription>
            Thanks{user?.name ? `, ${user.name}` : ""} — your request is in. A
            platform administrator is reviewing it, and your workspace unlocks
            the moment you're approved.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <dl className="flex flex-col gap-2 text-sm">
            <DetailRow label="Name" value={user?.name ?? "—"} />
            <DetailRow label="Email" value={user?.email ?? "—"} />
            <DetailRow label="Status" value="Under review" />
          </dl>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => logout()}
            >
              <LogOut className="mr-1.5 size-4" />
              Sign out
            </Button>
            <Button size="sm" onClick={() => void checkStatus()} disabled={checking}>
              {checking ? (
                <Loader2 className="mr-1.5 size-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-1.5 size-4" />
              )}
              Check status
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface-elevated px-3 py-2">
      <dt className="text-xs text-on-surface-muted">{label}</dt>
      <dd className="truncate text-xs font-medium text-on-surface">{value}</dd>
    </div>
  );
}
